import {
  cond,
  T,
  pathOr,
  equals,
  split,
  filter,
  propEq,
  head,
  view,
  dropLast,
  lensPath,
  values,
  includes,
  difference,
  path,
  always,
  map,
} from 'ramda';
import { isNotEmpty } from 'src/utilities/hasValue';
import { META_DATA } from 'src/services/constants';
import { filterDocument } from 'src/utilities/document';
import {
  IS_API_UPDATE,
  LENDSMART_SH_API_RESOURCE_READ,
  LENDSMART_SH_API_RESOURCE_WRITE,
  LENDSMART_SH_FIELD,
} from 'src/features/core/Intents/intentConstants';

export const splitTheLocationString = (locationString: string) => {
  return isNotEmpty(locationString) ? split('/', locationString) : [];
};

export const isDataFoundInArrayType = (intent: any) => {
  return isNotEmpty(
    filter(propEq('type', 'ARRAY'), pathOr([], ['save_in_api_field'], intent))
  );
};

export const mkAutoAnswerQuestion = (intent: any, apiResourceData: any) => {
  return cond([
    [
      () => equals('false', pathOr('true', [META_DATA, IS_API_UPDATE], intent)),
      always({
        answer: pathOr('', ['answer'], intent),
        hideThisIntent: false,
      })
    ],
    [
      () =>
        equals('AI', pathOr('', ['is_mask_this_intent_cond'], intent)) &&
        !isDataFoundInArrayType(intent),
      () => getAutoAnswerForQuestions(intent, apiResourceData, true),
    ],
    [
      () =>
        equals('AI', pathOr('', ['is_mask_this_intent_cond'], intent)) &&
        isDataFoundInArrayType(intent),
      () => getAutoAnswerForArrayTypeData(intent, apiResourceData, true),
    ],
    [
      () =>
        equals(
          'RECONCILE_DOCUMENT',
          pathOr('', ['is_mask_this_intent_cond'], intent)
        ),
      () => watchWhetherDocumentUploaded(intent, apiResourceData, true),
    ],
    [
      () => isDataFoundInArrayType(intent),
      () => getAutoAnswerForArrayTypeData(intent, apiResourceData, false),
    ],
    [
      () => !isDataFoundInArrayType(intent),
      () => getAutoAnswerForPathTypeData(intent, apiResourceData),
    ],
    [T, () => getAutoAnswerForQuestions(intent, apiResourceData, false)],
  ])(pathOr('', ['is_mask_this_intent_cond'], intent));
};

export const watchWhetherDocumentUploaded = (
  userIntent: any,
  apiResourceData: any,
  hideThisIntent: boolean
) => {
  const documentApiResource = pathOr([], ['documents'], apiResourceData);
  if (
    isNotEmpty(
      filterDocument(
        documentApiResource,
        pathOr('', ['metadata', 'category'], userIntent)
      )
    )
  ) {
    return {
      answer: 'completed',
      hideThisIntent: false,
    };
  }
  return {
    answer: '',
    hideThisIntent: false,
  };
};

export const getAutoAnswerForPathTypeData = (
  userIntent: any,
  apiResourceData: any
) => {
  const apiResourceForThisIntent = path(
    [pathOr('', [META_DATA, LENDSMART_SH_API_RESOURCE_WRITE], userIntent)],
    apiResourceData
  );

  const savedInAPI = head(pathOr([], ['save_in_api_field'], userIntent));
  const splittedSavedPath: any = splitTheSavedDataPath(
    pathOr('', ['mapped_for'], savedInAPI)
  );
  const answer = view(lensPath(splittedSavedPath), apiResourceForThisIntent);
  if (!isNotEmpty(answer)) {
    return {
      answer: pathOr('', ['answer'], userIntent),
      hideThisIntent: false,
    };
  }
  return { answer, hideThisIntent: false };
};
export const getAutoAnswerForArrayTypeData = (
  userIntent: any,
  apiResourceData: any,
  hideThisIntent: boolean
) => {
  const apiResourceForThisIntent: any =
    path([
      pathOr('', [META_DATA, LENDSMART_SH_API_RESOURCE_WRITE], userIntent)
    ], apiResourceData);
  const savedInAPI = pathOr([], ['save_in_api_field'], userIntent);
  const filterIdentifierKeyForThisSavedData = head(
    filter(
      (data: any) => isNotEmpty(pathOr('', ['mapped_field_key'], data)),
      savedInAPI
    )
  );
  const splittedSavedPath: any = splitTheSavedDataPath(
    pathOr('', ['mapped_for'], filterIdentifierKeyForThisSavedData)
  );
  const savedPath: any = dropLast(1, splittedSavedPath);
  const identifiedKey = pathOr(
    [],
    ['mapped_field_key'],
    filterIdentifierKeyForThisSavedData
  );
  const filterArrayStoredInAPIResource: any = pathOr([]
  [savedPath],
    apiResourceForThisIntent
  );
  const filterTheParticularObject = head(
    filter(
      (array: any) => includes(identifiedKey, values(array)),
      filterArrayStoredInAPIResource
    )
  );
  const answer = head(
    difference(values(filterTheParticularObject), [identifiedKey])
  );
  if (!isNotEmpty(answer)) {
    return { answer: pathOr('', ['answer'], userIntent), hideThisIntent };
  }
  return { answer, hideThisIntent: false };
};

export const getAutoAnswerForQuestions = (
  userIntent: any,
  apiResourceData: any,
  hideThisIntent: boolean
) => {
  const aiDataIsIn = pathOr(
    '',
    [META_DATA, LENDSMART_SH_API_RESOURCE_READ],
    userIntent
  );
  const aiScrappedData = path([aiDataIsIn], apiResourceData);
  const identifier = splitTheLocationString(
    pathOr('', [META_DATA, LENDSMART_SH_FIELD], userIntent)
  );
  const answerFromAiScrappedData = pathOr('', identifier, aiScrappedData);
  const alreadyAnswered = pathOr('', ['answer'], userIntent);
  if (isNotEmpty(answerFromAiScrappedData) && !isNotEmpty(alreadyAnswered)) {
    return { answer: answerFromAiScrappedData, hideThisIntent };
  }
  return { answer: alreadyAnswered, hideThisIntent: false };
};

export const splitTheSavedDataPath = (data: string) => {
  return split('/', data);
};

export const splitTheSavedDataPathWithDot = (data: string) => {
  return split('.', data);
};

export const parsePathValue = (path: string, input: any) => {
  return pathOr('', splitTheSavedDataPathWithDot(path), input)
}

/**
 * Splits a string by dots and retains numeric values as numbers.
 *
 * @param {string} data - The input string to split.
 * @returns {(string | number)[]} An array of strings and numbers.
 * 
 *  @example
 * const data: string = 'asset_details.0.age';
 * const result = splitAndRetainNumeric(data);
 * console.log(result); // Output: ['asset_details', 0, 'age']
 */
export const splitAndRetainNumeric = (data: string) => {
  const splitData = split('.', data) || [];
  return map((item: any) => {
    const numericValue = parseFloat(item);
    return isNaN(numericValue) ? item : numericValue;
  }, splitData);
};


export const splitOnPipeChr = (data: string) => {
  return split('|', data);
};
